import React from 'react';
import { Phone, FileText, CheckCircle, MapPin, AlertTriangle } from 'lucide-react';
export function DotNumberPage() {
  const requiredStates = ['Alabama', 'Alaska', 'Arizona', 'Colorado', 'Florida', 'Georgia', 'Iowa', 'Kansas', 'Kentucky', 'Maine', 'Michigan', 'Minnesota', 'Missouri', 'New York', 'Ohio', 'Oklahoma', 'Oregon', 'South Dakota', 'Tennessee', 'Utah', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];
  return <div className="min-h-screen bg-gray-50" data-id="element-5233">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-5234">
        <div className="container mx-auto px-4" data-id="element-5235">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-5236">
            What is a USDOT Number?
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-5237">
            Understanding Federal Motor Carrier Registration Requirements
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-5238">
        <div className="max-w-4xl mx-auto" data-id="element-5239">
          {/* What is a USDOT Number */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5240">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-5241">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-5242" />
              USDOT Number Overview
            </h2>

            <div className="space-y-6" data-id="element-5243">
              <p className="text-lg text-gray-700" data-id="element-5244">
                Companies that operate commercial vehicles transporting
                passengers or hauling cargo in interstate commerce must be
                registered with the FMCSA and must have a USDOT Number.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-5245">
                <p className="text-gray-800 mb-3" data-id="element-5246">
                  <strong data-id="element-5247">Also required for:</strong>
                </p>
                <p className="text-gray-700" data-id="element-5248">
                  Commercial intrastate hazardous materials carriers who haul
                  quantities requiring a safety permit must register for a USDOT
                  Number.
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-5249">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5250">
                  Purpose of USDOT Number
                </h3>
                <p className="text-gray-700" data-id="element-5251">
                  The USDOT Number serves as a unique identifier when collecting
                  and monitoring a company's safety information acquired during:
                </p>
                <ul className="mt-3 space-y-2 text-gray-700" data-id="element-5252">
                  <li className="flex items-start" data-id="element-5253">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5254" />
                    <span data-id="element-5255">Audits</span>
                  </li>
                  <li className="flex items-start" data-id="element-5256">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5257" />
                    <span data-id="element-5258">Compliance reviews</span>
                  </li>
                  <li className="flex items-start" data-id="element-5259">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5260" />
                    <span data-id="element-5261">Crash investigations</span>
                  </li>
                  <li className="flex items-start" data-id="element-5262">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5263" />
                    <span data-id="element-5264">Inspections</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* New-Entrant Program */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-5265">
            <h2 className="text-3xl font-bold mb-6" data-id="element-5266">New-Entrant Program</h2>

            <div className="space-y-4" data-id="element-5267">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-5268">
                <p className="text-lg mb-4" data-id="element-5269">
                  All first-time carrier applicants for a USDOT Number will be
                  automatically enrolled in the{' '}
                  <strong data-id="element-5270">FMCSA New Entrant Safety Assurance Program</strong>.
                </p>

                <div className="space-y-3" data-id="element-5271">
                  <div className="flex items-start" data-id="element-5272">
                    <AlertTriangle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5273" />
                    <div data-id="element-5274">
                      <p className="font-semibold mb-1" data-id="element-5275">
                        Program Requirements:
                      </p>
                      <p className="text-gray-100" data-id="element-5276">
                        New entrants must pass a safety audit and maintain
                        acceptable roadside safety performance over an initial
                        18-month period before they are given permanent
                        registration status.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 text-gray-900 rounded-lg p-6" data-id="element-5277">
                <h3 className="font-bold mb-2" data-id="element-5278">Exemptions</h3>
                <p data-id="element-5279">
                  In most cases, companies operating exclusively as brokers or
                  non-vehicle-operating shippers or freight forwarders do not
                  need to obtain a USDOT Number.
                </p>
              </div>
            </div>
          </div>

          {/* State Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5280">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-5281">
              <MapPin className="mr-3 text-green-600" size={28} data-id="element-5282" />
              State-Specific Requirements
            </h2>

            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 mb-6" data-id="element-5283">
              <p className="text-lg text-gray-800 mb-4" data-id="element-5284">
                <strong data-id="element-5285">
                  In select states, all registrants of commercial motor
                  vehicles, even intrastate and non-Motor Carrier registrants,
                  are required to obtain a USDOT Number as a necessary condition
                  for commercial vehicle registration.
                </strong>
              </p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-5286">
              States Requiring USDOT Numbers (24 States):
            </h3>

            <div className="grid md:grid-cols-3 gap-3" data-id="element-5287">
              {requiredStates.map(state => <div key={state} className="bg-green-100 rounded-lg p-3 flex items-center" data-id="element-5288">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-5289" />
                  <span className="text-gray-800 font-medium" data-id="element-5290">{state}</span>
                </div>)}
            </div>
          </div>

          {/* Who Needs a USDOT Number */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5291">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5292">
              Who Needs a USDOT Number?
            </h2>

            <div className="space-y-4" data-id="element-5293">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-5294">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5295">
                  ✅ Required For:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-5296">
                  <li className="flex items-start" data-id="element-5297">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5298" />
                    <span data-id="element-5299">
                      Companies operating commercial vehicles in interstate
                      commerce (passengers or cargo)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-5300">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5301" />
                    <span data-id="element-5302">
                      Intrastate hazardous materials carriers requiring a safety
                      permit
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-5303">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5304" />
                    <span data-id="element-5305">
                      All commercial motor vehicle registrants in the 24
                      required states (even intrastate)
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6" data-id="element-5306">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5307">
                  ❌ Generally NOT Required For:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-5308">
                  <li className="flex items-start" data-id="element-5309">
                    <span className="mr-2" data-id="element-5310">•</span>
                    <span data-id="element-5311">Companies operating exclusively as brokers</span>
                  </li>
                  <li className="flex items-start" data-id="element-5312">
                    <span className="mr-2" data-id="element-5313">•</span>
                    <span data-id="element-5314">Non-vehicle-operating shippers</span>
                  </li>
                  <li className="flex items-start" data-id="element-5315">
                    <span className="mr-2" data-id="element-5316">•</span>
                    <span data-id="element-5317">Freight forwarders (without vehicles)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Key Points Summary */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-5318">
            <h2 className="text-3xl font-bold mb-6" data-id="element-5319">Key Points to Remember</h2>

            <div className="space-y-3" data-id="element-5320">
              <div className="flex items-start" data-id="element-5321">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5322" />
                <span data-id="element-5323">
                  USDOT Number is a unique identifier for safety monitoring
                </span>
              </div>
              <div className="flex items-start" data-id="element-5324">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5325" />
                <span data-id="element-5326">
                  Required for interstate commerce and certain intrastate
                  operations
                </span>
              </div>
              <div className="flex items-start" data-id="element-5327">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5328" />
                <span data-id="element-5329">
                  New entrants automatically enrolled in 18-month safety
                  assurance program
                </span>
              </div>
              <div className="flex items-start" data-id="element-5330">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5331" />
                <span data-id="element-5332">
                  24 states require USDOT Numbers for all commercial vehicle
                  registrations
                </span>
              </div>
              <div className="flex items-start" data-id="element-5333">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5334" />
                <span data-id="element-5335">Brokers and non-vehicle operators generally exempt</span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-5336">
            <h2 className="text-3xl font-bold mb-4" data-id="element-5337">
              Need Help with DOT Compliance?
            </h2>
            <p className="text-xl mb-6" data-id="element-5338">
              Expert guidance on USDOT Number requirements and registration
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-5339">
              <Phone className="mr-3" size={24} data-id="element-5340" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-5341">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}